import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getToken, setToken as persistToken, clearToken } from "../api/http";
import type { AdminUser } from "../api/admin";

type AuthState = {
  token: string | null;
  user: AdminUser | null;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
};

const USER_KEY = "medreg_admin_user";
const AuthCtx = createContext<AuthState | null>(null);

function readUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AdminUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTok] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<AdminUser | null>(() => readUser());

  const login = (t: string, u: AdminUser) => {
    persistToken(t);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setTok(t);
    setUser(u);
  };

  const logout = () => {
    clearToken();
    localStorage.removeItem(USER_KEY);
    setTok(null);
    setUser(null);
  };

  useEffect(() => {
    const onLogout = () => logout();
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  return <AuthCtx.Provider value={{ token, user, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
